import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import {
    NAME_PATTERN,
    NAME_PATTERN_MESSAGE,
    DIGITS_ONLY_PATTERN,
    PHONE_10_DIGIT_PATTERN,
    PHONE_10_DIGIT_MESSAGE,
} from "@demandproai/shared";
import useAxios from "../../../hooks/useAxios";
import Constants from "../../../Constants";
import { toaster } from "../../../utils/toast";
import { useDiscoveryOverviewContext } from "../../../contexts/DiscoveryOverviewContext";

const createEmptyAttorney = () => ({ name: '', phoneNumber: '', barNumber: '' });

const validationSchema = Yup.object({
    address: Yup.string().trim().required('Address is required').max(500, 'Address is too long'),
    attorneys: Yup.array().of(
        Yup.object({
            name: Yup.string()
                .trim()
                .required('Name is required')
                .matches(NAME_PATTERN, NAME_PATTERN_MESSAGE)
                .max(100, 'Name is too long'),
            phoneNumber: Yup.string()
                .trim()
                .required('Phone Number is required')
                .matches(PHONE_10_DIGIT_PATTERN, PHONE_10_DIGIT_MESSAGE),
            barNumber: Yup.string()
                .trim()
                .required('Bar Number is required')
                .matches(DIGITS_ONLY_PATTERN, 'Bar Number must contain digits only')
                .max(20, 'Bar Number is too long'),
        }),
    ),
});

const buildInitialValues = (discoverySetting) => ({
    address: discoverySetting?.address ?? '',
    caseId: discoverySetting?.caseId ?? '',
    shouldIncludeQuestionText: discoverySetting?.shouldIncludeQuestionText ?? true,
    isDefaultDiscoverySetting: false,
    attorneys: discoverySetting?.attorneys?.length
        ? discoverySetting.attorneys.map((values) => ({
            attorneyId: values.attorneyId ?? '',
            name: values.name ?? '',
            phoneNumber: values.phoneNumber ?? '',
            barNumber: values.barNumber ?? '',
        }))
        : [createEmptyAttorney()],
});

const useDiscoverySettingForm = () => {
    const { caseId } = useParams();
    const { postData } = useAxios();
    const { discoverySetting, setIsDiscoverySettingModal, fetchCaseLevelDiscoverySetting } = useDiscoveryOverviewContext();

    const [selectedAttorneyIndex, setSelectedAttorneyIndex] = useState(0);

    const handleSave = useCallback(async (values) => {
        const payload = {
            caseId: caseId,
            address: values.address,
            shouldIncludeQuestionText: values.shouldIncludeQuestionText,
            attorneys: values.attorneys,
            isDefaultDiscoverySetting: values.isDefaultDiscoverySetting
        };

        const { success, message } = await postData(Constants.ApiUrl.discovery.addEditDiscoverySetting, payload);
        if (!success) {
            toaster({ message: message || 'Failed to save', success: false });
            return;
        }
        await fetchCaseLevelDiscoverySetting();
        toaster({ message: message || 'Saved', success: true });
        setIsDiscoverySettingModal(false);
    }, [caseId, postData, fetchCaseLevelDiscoverySetting, setIsDiscoverySettingModal]);

    const initialValues = useMemo(() => buildInitialValues(discoverySetting), [discoverySetting]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSave,
    });

    const getErrorMessage = useCallback(
        (field) => {
            const meta = formik.getFieldMeta(field);
            return meta.touched && meta.error;
        },
        [formik],
    );

    const hasAttorneyError = useCallback(
        (index) => {
            const attorneyErrors = formik.errors.attorneys?.[index];
            if (!attorneyErrors) return false;
            const touchedFields = formik.touched.attorneys?.[index] ?? {};
            return Object.keys(attorneyErrors).some((field) => touchedFields[field]);
        },
        [formik.errors.attorneys, formik.touched.attorneys],
    );

    const addAttorney = useCallback(() => {
        const nextAttorneys = [...formik.values.attorneys, createEmptyAttorney()];
        formik.setFieldValue('attorneys', nextAttorneys);
        setSelectedAttorneyIndex(nextAttorneys.length - 1);
    }, [formik]);

    // removal is local only, persisted on save
    const removeAttorney = useCallback((index) => {
        const next = formik.values.attorneys.filter((_, i) => i !== index);
        formik.setFieldValue('attorneys', next);
        const touchedAttorneys = formik.touched.attorneys;
        if (Array.isArray(touchedAttorneys)) {
            formik.setFieldTouched('attorneys', touchedAttorneys.filter((_, i) => i !== index), false);
        }
        setSelectedAttorneyIndex((i) => Math.max(0, Math.min(i, next.length - 1)));
    }, [formik]);

    return {
        formik,
        getErrorMessage,
        hasAttorneyError,
        addAttorney,
        removeAttorney,
        selectedAttorneyIndex,
        setSelectedAttorneyIndex,
    };
};

export default useDiscoverySettingForm;
